import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { t } from "@/lib/i18n";
import { altOf, assetUrl } from "@/lib/content";
import { srcSetOf } from "@/components/Photo";
import { StockBadge } from "@/components/Pending";

const btn = "inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors";

export default function Lightbox({ items, index, onChange, onClose, isPreview }) {
  const ref = useRef(null);
  const touch = useRef(null);
  const item = items[index];
  const go = useCallback((d) => onChange((index + d + items.length) % items.length), [index, items.length, onChange]);

  useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("[data-autofocus]")?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, []);

  const onKey = (e) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowRight") go(1);
    else if (e.key === "ArrowLeft") go(-1);
    else if (e.key === "Tab") {
      const f = [...ref.current.querySelectorAll("button")];
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={t("galeri.dialog")}
      onKeyDown={onKey}
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[60] bg-[#0E1A13]/95 flex flex-col fade-in"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="gallery-lightbox"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white/80 text-sm">
        <span aria-live="polite" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
        <button type="button" className={btn} onClick={onClose} aria-label={t("galeri.close")} data-autofocus data-testid="lightbox-close-button">
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div className="relative flex-1 flex items-center justify-center px-2 sm:px-20 min-h-0" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <figure className="relative max-h-full max-w-full flex flex-col items-center">
          <img key={item.id} src={assetUrl(item.src)} srcSet={srcSetOf(item)} sizes="100vw" alt={altOf(item, isPreview)} className="max-h-[78vh] max-w-full w-auto object-contain rounded-lg fade-in select-none" draggable="false" data-testid="lightbox-image" />
          {isPreview && item.stock && <StockBadge className="absolute top-3 left-3" />}
          <figcaption className="mt-3 text-center text-sm text-white/75 max-w-xl px-4">{altOf(item, isPreview)}</figcaption>
        </figure>
        <button type="button" className={`${btn} absolute left-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex`} onClick={() => go(-1)} aria-label={t("galeri.prev")} data-testid="lightbox-prev-button">
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <button type="button" className={`${btn} absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex`} onClick={() => go(1)} aria-label={t("galeri.next")} data-testid="lightbox-next-button">
          <ChevronRight className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div className="flex sm:hidden justify-center gap-4 pb-4 pt-2">
        <button type="button" className={btn} onClick={() => go(-1)} aria-label={t("galeri.prev")} data-testid="lightbox-prev-button-mobile"><ChevronLeft className="h-6 w-6" aria-hidden="true" /></button>
        <button type="button" className={btn} onClick={() => go(1)} aria-label={t("galeri.next")} data-testid="lightbox-next-button-mobile"><ChevronRight className="h-6 w-6" aria-hidden="true" /></button>
      </div>
    </div>,
    document.body
  );
}
